import { useCallback, useEffect, useState, type CSSProperties } from 'react'

// Admin -> Session Codes (reports db/110, 2026-10-15).
// Every MERAC session code the Sync Hub has scanned (Scan-ReportSessionCodes,
// 06:00 daily + "Scan now"), with what MERAC knows about it and where it is or
// could be used on reports.nymr.co.uk. Tick Active (+ set Menu) to bring a code
// into the Pullman Bookings feed; Source says which pipeline carries it.

interface Row {
  code: string
  name: string | null
  shd_session_type: number | null
  source: 'Direct' | 'SSRSReport'
  is_active: boolean
  menu_courses: 'multi' | 'single' | 'none'
  sessions_total: number | null
  sessions_future: number | null
  first_session: string | null
  last_session: string | null
  seat_bookings: number | null
  food_lines: number | null
  table_menus_type: string | null
  seating_plan: string | null
  first_seen: string | null
  last_seen: string | null
  scanned_at: string | null
  updated_by: string | null
  updated_at: string | null
  notes: string | null
  used_in: string[]
  can_use: string[]
}
interface Payload { rows: Row[]; scanned_at: string | null }
interface ScanStatus {
  last: { id: number; status: string; requested_by: string; requested_at: string | null; completed_at: string | null; message: string | null; duration_ms: number | null } | null
  in_flight: boolean; locked: boolean; next_available_at: string | null; lockout_minutes: number; can_request: boolean
}

const TH: CSSProperties = { textAlign: 'left', padding: '6px 8px', color: '#6b6b6b', fontWeight: 500, fontSize: '0.78rem', borderBottom: '1px solid #e5e1d6', whiteSpace: 'nowrap', position: 'sticky', top: 0, background: '#fff' }
const TD: CSSProperties = { padding: '6px 8px', fontSize: '0.8rem', borderBottom: '1px solid #f0ece1', verticalAlign: 'top' }
const chip = (bg: string, fg: string): CSSProperties => ({ display: 'inline-block', padding: '1px 8px', borderRadius: 10, fontSize: '0.72rem', background: bg, color: fg, marginRight: 4, marginBottom: 2 })
const select: CSSProperties = { padding: '3px 6px', fontSize: '0.78rem', border: '1px solid #ccc', borderRadius: 6, background: '#fff' }

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso.length === 10 ? iso + 'T00:00:00' : iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}
function fmtDateTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })
}

export function AdminSessionCodes() {
  const [data, setData] = useState<Payload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState<string | null>(null)
  const [opError, setOpError] = useState<string | null>(null)
  const [scan, setScan] = useState<ScanStatus | null>(null)
  const [showAll, setShowAll] = useState(false)
  const [filter, setFilter] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [r, s] = await Promise.all([fetch('/api/access/session-codes'), fetch('/api/access/session-codes/scan')])
      if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.error || `Request failed (${r.status})`) }
      setData(await r.json()); setError(null)
      if (s.ok) setScan(await s.json())
    } catch (e) { setError(e instanceof Error ? e.message : String(e)) } finally { setLoading(false) }
  }, [])
  useEffect(() => { void load() }, [load])

  // While a scan is queued or running, poll until it finishes, then reload the rows.
  useEffect(() => {
    if (!scan?.in_flight) return
    const t = window.setInterval(async () => {
      const s = await fetch('/api/access/session-codes/scan').then((r) => (r.ok ? r.json() : null)).catch(() => null) as ScanStatus | null
      if (s) { setScan(s); if (!s.in_flight) void load() }
    }, 5000)
    return () => window.clearInterval(t)
  }, [scan?.in_flight, load])

  const patch = async (code: string, body: Partial<Pick<Row, 'is_active' | 'source' | 'menu_courses' | 'notes'>>) => {
    setSaving(code); setOpError(null)
    try {
      const r = await fetch(`/api/access/session-codes/${encodeURIComponent(code)}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      const b = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(b.error || `PATCH returned ${r.status}`)
      setData((d) => d ? { ...d, rows: d.rows.map((x) => (x.code === code && b.row ? b.row : x)) } : d)
    } catch (e) { setOpError(e instanceof Error ? e.message : String(e)) } finally { setSaving(null) }
  }

  const requestScan = async () => {
    setOpError(null)
    const r = await fetch('/api/access/session-codes/scan', { method: 'POST' })
    const b = await r.json().catch(() => ({}))
    if (!r.ok && r.status !== 202) { setOpError(b.error || `Scan request returned ${r.status}`); return }
    setScan(b)
  }

  const rows = (data?.rows ?? [])
    .filter((r) => showAll || r.is_active || (r.sessions_future ?? 0) > 0)
    .filter((r) => !filter || `${r.code} ${r.name ?? ''} ${r.table_menus_type ?? ''}`.toLowerCase().includes(filter.toLowerCase()))
  const newInactive = (data?.rows ?? []).filter((r) => !r.is_active && (r.sessions_future ?? 0) > 0).length

  return (
    <>
      <header className="events-admin__head">
        <p className="admin-subtle" style={{ marginTop: 0, marginBottom: 0 }}>
          MERAC session codes found by the Sync Hub (last / next 365 days). <strong>Active</strong> codes feed the Pullman Bookings
          tab (Bookings, Customers, Daily Bookings, Menu Exceptions, Summary, Audit); set <strong>Menu</strong> to how the service&apos;s
          food is booked so expected courses are right. New codes arrive unticked and IT Support is emailed.
          {data?.scanned_at && <> Last scan {fmtDateTime(data.scanned_at)}.</>}
        </p>
        <button type="button" className="btn-primary" onClick={requestScan} disabled={!scan || !scan.can_request}
          title={scan?.in_flight ? 'A scan is running' : scan?.locked ? `Available again at ${fmtDateTime(scan.next_available_at)}` : 'Scan MERAC for session codes now'}>
          {scan?.in_flight ? 'Scanning…' : 'Scan now'}
        </button>
      </header>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', margin: '10px 0', flexWrap: 'wrap' }}>
        <input type="search" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter code or name…" style={{ padding: '6px 10px', fontSize: '0.85rem', border: '1px solid #ccc', borderRadius: 8, minWidth: 240 }} aria-label="Filter session codes" />
        <label style={{ fontSize: '0.8rem', color: '#0b3d2e', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} /> Show codes with no future sessions
        </label>
        {newInactive > 0 && <span style={chip('#fdf1dc', '#8a5a00')}>{newInactive} code{newInactive === 1 ? '' : 's'} with future sessions not active</span>}
        {scan?.last && <span style={{ fontSize: '0.75rem', color: '#8a8577' }}>Last scan request: {scan.last.status} {scan.last.completed_at ? fmtDateTime(scan.last.completed_at) : ''}{scan.last.message ? ` — ${scan.last.message}` : ''}</span>}
      </div>

      {opError && <p style={{ color: '#a33' }}>Couldn&apos;t save: {opError}</p>}
      {loading && <p>Loading session codes…</p>}
      {error && <p style={{ color: '#a33' }}>Couldn&apos;t load: {error}</p>}

      {!loading && !error && (
        <div style={{ overflowX: 'auto', maxHeight: '70vh', overflowY: 'auto', border: '1px solid #e5e1d6', borderRadius: 8, background: '#fff' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 1200 }}>
            <thead>
              <tr>
                <th style={TH}>Active</th><th style={TH}>Code</th><th style={TH}>Name</th><th style={TH}>Source</th><th style={TH}>Menu</th>
                <th style={{ ...TH, textAlign: 'right' }}>Sessions (future / total)</th><th style={TH}>Last session</th>
                <th style={{ ...TH, textAlign: 'right' }}>Seat bookings</th><th style={{ ...TH, textAlign: 'right' }}>Food lines</th>
                <th style={TH}>Used in</th><th style={TH}>Could be used in</th><th style={TH}>Seating plan</th><th style={TH}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.code} style={{ opacity: saving === r.code ? 0.6 : 1, background: r.is_active ? undefined : '#fbfaf6' }}>
                  <td style={TD}><input type="checkbox" checked={r.is_active} disabled={saving === r.code} onChange={(e) => patch(r.code, { is_active: e.target.checked })} aria-label={`${r.code} active`} /></td>
                  <td style={{ ...TD, fontWeight: 700, color: '#0b3d2e', whiteSpace: 'nowrap' }}>{r.code}</td>
                  <td style={TD}>{r.name ?? '—'}{r.shd_session_type ? <span style={{ color: '#8a8577', fontSize: '0.72rem' }}> ({r.shd_session_type})</span> : null}</td>
                  <td style={TD}>
                    <select value={r.source} style={select} disabled={saving === r.code} onChange={(e) => patch(r.code, { source: e.target.value as Row['source'] })} aria-label={`${r.code} source`}>
                      <option value="Direct">Direct</option><option value="SSRSReport">SSRSReport</option>
                    </select>
                  </td>
                  <td style={TD}>
                    <select value={r.menu_courses} style={select} disabled={saving === r.code} onChange={(e) => patch(r.code, { menu_courses: e.target.value as Row['menu_courses'] })} aria-label={`${r.code} menu`}>
                      <option value="multi">Multi-course</option><option value="single">Single course</option><option value="none">No menu</option>
                    </select>
                  </td>
                  <td style={{ ...TD, textAlign: 'right', whiteSpace: 'nowrap' }}>{r.sessions_future ?? 0} / {r.sessions_total ?? 0}</td>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>{fmtDate(r.last_session)}</td>
                  <td style={{ ...TD, textAlign: 'right' }}>{r.seat_bookings ?? 0}</td>
                  <td style={{ ...TD, textAlign: 'right' }}>{r.food_lines ?? 0}</td>
                  <td style={TD}>{r.used_in.length ? r.used_in.map((u) => <span key={u} style={chip('#eaf2ee', '#0b3d2e')}>{u}</span>) : <span style={{ color: '#8a8577' }}>—</span>}</td>
                  <td style={TD}>{r.can_use.length ? r.can_use.map((u) => <span key={u} style={chip('#fdf1dc', '#8a5a00')}>{u}</span>) : <span style={{ color: '#8a8577' }}>—</span>}</td>
                  <td style={TD}>{r.seating_plan ?? '—'}</td>
                  <td style={TD}>
                    <input type="text" defaultValue={r.notes ?? ''} placeholder="Notes" style={{ width: 180, padding: '3px 6px', fontSize: '0.78rem', border: '1px solid #ddd', borderRadius: 6 }}
                      onBlur={(e) => { const v = e.target.value.trim(); if (v !== (r.notes ?? '')) void patch(r.code, { notes: v || null }) }} aria-label={`${r.code} notes`} />
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td style={TD} colSpan={13}>No session codes{filter ? ` match “${filter}”` : ' yet — run Scan now'}.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <p style={{ fontSize: '0.72rem', color: '#8a8577', margin: '8px 0 0' }}>
        Source: <em>Direct</em> = the Sync Hub pulls the bookings itself (Colin&apos;s DN_001 SQL with these codes injected); <em>SSRSReport</em> = the bookings still come through Colin&apos;s MERAC report, whose own yearly list must include the code. Seat bookings and food lines are counts in the scan window.
      </p>
    </>
  )
}
